import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeftRight, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn.js";

/** The design's slide, as variants so the direction can be passed as custom. */
const SLIDE = {
  enter: (from: number) => ({ x: from > 0 ? 320 : -320, opacity: 0, scale: 0.94 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (from: number) => ({ x: from > 0 ? -320 : 320, opacity: 0, scale: 0.94 }),
};

export interface DeckCard {
  id: string;
  /** The widget's own name, used on the jump chip and in the announcement. */
  name: string;
  /** The design's small category caption above the card. */
  category: string;
  node: React.ReactNode;
}

/**
 * The design's mobile swipe deck: one widget at a time, swiped or stepped.
 *
 * Below the tablet breakpoint the bento becomes a single column of nine tall
 * cards, which is a long scroll to reach the last one. The deck is the
 * design's answer and it is kept.
 *
 * **The swipe is not the only way through it, and that is the port's one
 * substantive change.** The design ships drag gestures and nothing else: no
 * keyboard path, no focus handling, and dots that are unlabelled buttons. A
 * gesture-only control is unreachable for anyone not using touch, which
 * section 4 forbids outright. So the same movement is available from the
 * previous and next buttons, from the named jump chips, from the pagination
 * dots and from the left and right arrow keys, and which card is showing is
 * announced rather than only drawn.
 *
 * `<MotionConfig reducedMotion="user">` in `main.tsx` covers the slide for
 * anyone who has asked for less movement; the deck still works, it just cuts.
 */
export function MobileSwipeStatsDeck({
  cards,
  className,
}: {
  cards: readonly DeckCard[];
  className?: string;
}) {
  const [at, setAt] = useState(0);
  const [direction, setDirection] = useState(0);

  // A widget hidden in the customiser shortens the deck underneath whoever is
  // reading it, which would otherwise leave the index past the end.
  useEffect(() => {
    setAt((current) => (current >= cards.length ? Math.max(0, cards.length - 1) : current));
  }, [cards.length]);

  if (cards.length === 0) return null;

  const step = (by: number) => {
    setDirection(by);
    setAt((current) => (current + by + cards.length) % cards.length);
  };

  const jump = (to: number) => {
    setDirection(to > at ? 1 : -1);
    setAt(to);
  };

  const card = cards[at] ?? cards[0];
  if (card === undefined) return null;

  return (
    <section
      aria-label="Widget deck"
      className={cn("mx-auto flex w-full max-w-lg select-none flex-col gap-3", className)}
    >
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-6 w-6 items-center justify-center rounded-lg border border-accent-2/30 bg-accent-2/15 text-accent-2-ink"
          >
            <ArrowLeftRight size={13} />
          </span>
          <div>
            <p className="tabular text-xs font-bold uppercase tracking-tight text-ink">Deck</p>
            <p className="text-[11px] text-ink-muted">Swipe, or use the arrows</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Announced, so a reader who cannot see the card change is told the
              deck moved. The design drew this and said nothing. */}
          <p
            role="status"
            className="tabular rounded-lg border border-line bg-line/40 px-2 py-1 text-xs font-medium text-ink-muted"
          >
            {/* The name is announced but not drawn: spelled out beside the
                counter it wrapped the pill onto three lines at 360, and the
                chip row underneath already shows which card is selected. */}
            <span className="font-bold text-ink">{String(at + 1)}</span>
            {` / ${String(cards.length)}`}
            <span className="sr-only">{`: ${card.name}`}</span>
          </p>
          <Stepper
            label="Previous widget"
            onClick={() => {
              step(-1);
            }}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </Stepper>
          <Stepper
            label="Next widget"
            onClick={() => {
              step(1);
            }}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </Stepper>
        </div>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto px-1 py-1">
        {cards.map((entry, index) => (
          <button
            key={entry.id}
            type="button"
            aria-current={index === at ? "true" : undefined}
            onClick={() => {
              jump(index);
            }}
            // The arrow keys move the deck while a chip has focus, which is
            // how a row of related buttons is expected to behave. The handler
            // lives on the buttons rather than on the section around them:
            // a keyboard listener on a non-interactive wrapper is one nothing
            // announces and nothing can reach.
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") step(1);
              if (event.key === "ArrowLeft") step(-1);
            }}
            className={cn(
              "shrink-0 whitespace-nowrap rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors",
              index === at
                ? "border-btn bg-btn font-semibold text-btn-ink"
                : "border-line bg-line/40 text-ink-muted hover:text-ink",
            )}
          >
            {entry.name}
          </button>
        ))}
      </div>

      <div className="relative min-h-[330px] w-full overflow-hidden rounded-3xl pt-1">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={card.id}
            custom={direction}
            variants={SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.25}
            onDragEnd={(_event, info) => {
              if (info.offset.x < -45 || info.velocity.x < -350) step(1);
              else if (info.offset.x > 45 || info.velocity.x > 350) step(-1);
            }}
            style={{ touchAction: "pan-y" }}
            className="w-full"
          >
            <p className="mb-2 px-1 text-[11px] font-medium uppercase tracking-wider text-ink-muted">
              {card.category}
            </p>
            {card.node}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between px-2 pt-1">
        <p className="text-[11px] text-ink-muted">Swipe the card, or use the arrow keys.</p>
        <div className="flex items-center gap-1.5">
          {cards.map((entry, index) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                jump(index);
              }}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                index === at ? "w-5 bg-accent-2" : "w-2 bg-line-strong",
              )}
            >
              {/* A name, because the design's dots have none at all and a row
                  of nine unnamed buttons is what axe reports. */}
              <span className="sr-only">{`Show ${entry.name}`}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stepper({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-line bg-line/40 p-1.5 text-ink-muted transition-colors hover:bg-line hover:text-ink"
    >
      <span className="sr-only">{label}</span>
      {children}
    </button>
  );
}
